/**
 * Text measuring helpers for hand-drawn SVG charts.
 *
 * SVG `<text>` neither wraps nor ellipsizes, so a chart that wants labels to fit
 * a fixed column has to measure them itself. Measurement goes through a canvas
 * context (same font metrics as the rendered text) and is cached, because the
 * charts re-measure the same labels on every render.
 */

const widthCache = new Map<string, number>();

let context: CanvasRenderingContext2D | null | undefined;

const getContext = (): CanvasRenderingContext2D | null => {
  if (context === undefined) {
    context = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  }
  return context;
};

/** Rendered width of `text` in px for a CSS `font` shorthand, e.g. "600 15px sans-serif". */
export function measureSvgText(text: string, font: string): number {
  const key = `${font}|${text}`;
  const cached = widthCache.get(key);
  if (cached !== undefined) return cached;

  const ctx = getContext();
  let width: number;
  if (ctx) {
    ctx.font = font;
    width = ctx.measureText(text).width;
  } else {
    // No canvas (tests/SSR): rough average glyph width for the given size.
    width = text.length * ((parseFloat(font.replace(/^\D*/, "")) || 11) * 0.6);
  }

  widthCache.set(key, width);
  return width;
}

/**
 * Longest prefix of `text` that fits `maxWidth`, with an ellipsis appended when
 * anything had to be dropped. Cutting on a character boundary keeps the label
 * readable, unlike an SVG clip path which slices through the middle of a glyph.
 */
export function fitSvgText(text: string, maxWidth: number, font: string): string {
  if (!text || maxWidth <= 0) return "";
  if (measureSvgText(text, font) <= maxWidth) return text;

  const ellipsis = "…";
  const ellipsisWidth = measureSvgText(ellipsis, font);
  if (ellipsisWidth > maxWidth) return "";

  // Binary search the longest prefix that fits together with the ellipsis.
  let low = 0;
  let high = text.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (measureSvgText(text.slice(0, mid), font) + ellipsisWidth <= maxWidth) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  return text.slice(0, low).trimEnd() + ellipsis;
}
